import React, { Fragment, useCallback, useState } from "react";
import DataTable from "react-data-table-component";
import { Alert, Badge, Spinner } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchTrialBalance } from "../../../api/accounting";
import { formatTZS } from "../../../utils/format";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runLoad } from "../Common/credoraToast";
import AccountingReportFilters from "./AccountingReportFilters";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import { accountingReportBreadcrumbs } from "./accountingReportShell";
import { formatAccountType } from "./accountingUtils";
import { useAccountingReportFilters } from "./useAccountingReportFilters";

const TrialBalancePage = ({ reportMode = false }) => {
  const crumbs = accountingReportBreadcrumbs({ reportMode, title: "Trial Balance" });
  const filters = useAccountingReportFilters();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return runLoad(() => fetchTrialBalance(filters.reportParams()).then(setData), {
      errorMessage: "Failed to load trial balance.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load trial balance.")))
      .finally(() => setLoading(false));
  }, [filters.reportParams]);

  const columns = [
    { name: "Code", selector: (r) => r.account_code, width: "90px" },
    { name: "Account", selector: (r) => r.account_name, grow: 2 },
    { name: "Type", selector: (r) => formatAccountType(r.account_type), width: "110px" },
    { name: "Debit", cell: (r) => (Number(r.debit) ? formatTZS(r.debit) : "—"), right: true, width: "130px" },
    { name: "Credit", cell: (r) => (Number(r.credit) ? formatTZS(r.credit) : "—"), right: true, width: "130px" },
  ];

  return (
    <Fragment>
      <Breadcrumbs mainTitle={crumbs.mainTitle} parent={crumbs.parent} title={crumbs.title} />
      <div className={`container-fluid${reportMode ? " credora-finance-reports pb-5 mb-3" : ""}`}>
        {error && <Alert color="danger">{error}</Alert>}
        <AccountingReportFilters {...filters} onApply={load} loading={loading} />
        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : data ? (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0">Trial balance</h5>
                <small className="text-muted">
                  {data.period_name || `${data.date_from || ""} – ${data.date_to || ""}`}
                  {data.branch_name ? ` · ${data.branch_name}` : ""}
                </small>
              </div>
              <Badge color={data.is_balanced ? "success" : "danger"}>
                {data.is_balanced ? "Balanced" : "Out of balance"}
              </Badge>
            </div>
            <div className="card-body p-0">
              <DataTable columns={columns} data={data.rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            </div>
            <div className="card-footer d-flex justify-content-end gap-4">
              <strong>Total debit: {formatTZS(data.total_debit)}</strong>
              <strong>Total credit: {formatTZS(data.total_credit)}</strong>
            </div>
          </div>
        ) : null}
      </div>
    </Fragment>
  );
};

export default TrialBalancePage;
